import { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInAnonymously
} from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { SECURITY_CONFIG } from '../config/security';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        try {
          const docRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            setUserDetails(docSnap.data());
          } else {
            // User exists in Firebase Auth but not in Firestore yet
            // This can happen briefly during loginAsGuest before setDoc completes
            setUserDetails(null);
          }
        } catch (error) {
          console.error("Error fetching user details:", error);
        }
      } else {
        setUserDetails(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signup = async (email, password, name) => {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);

    const role = SECURITY_CONFIG.ADMIN_EMAILS.includes(email.toLowerCase()) ? 'admin' : 'student';

    await setDoc(doc(db, "users", user.uid), {
      name,
      email,
      role,
      createdAt: new Date().toISOString(),
      checkedOutItems: []
    });

    return user;
  };

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = () => {
    return signOut(auth);
  };

  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  // Check if a student PID exists in the students collection
  const checkStudentPid = async (pid) => {
    if (!pid || !pid.trim()) {
      throw new Error('Student ID is required');
    }
    const trimmedPid = pid.trim();
    const studentsRef = collection(db, 'students');
    const q = query(studentsRef, where('pid', '==', trimmedPid));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null; // Student not found -- UI should prompt for name
    }
    return snapshot.docs[0].data();
  };

  const loginAsGuest = async (pid, studentName) => {
    if (!pid || !pid.trim()) {
      throw new Error('Student ID is required');
    }
    const trimmedPid = pid.trim();

    // Look up student in the students collection
    const studentData = await checkStudentPid(trimmedPid);

    // If student doesn't exist, a name must be provided to register
    if (!studentData && !studentName) {
      throw new Error('STUDENT_NOT_FOUND');
    }

    const name = studentData?.name || studentName || 'Student ' + trimmedPid;

    // If student doesn't exist yet, create them in the students collection
    if (!studentData) {
      await setDoc(doc(db, 'students', trimmedPid), {
        pid: trimmedPid,
        name: name,
        createdAt: new Date().toISOString()
      });
    }

    // Create a real Firebase anonymous auth session
    const { user } = await signInAnonymously(auth);

    // Create user profile in Firestore with student role
    await setDoc(doc(db, 'users', user.uid), {
      name: name,
      pid: trimmedPid,
      role: 'student',
      provider: 'anonymous',
      createdAt: new Date().toISOString(),
      checkedOutItems: []
    });

    return user;
  };

  const isAdmin = () => {
    return userDetails?.role === 'admin';
  };

  const isPidUser = () => {
    return currentUser?.isAnonymous === true || userDetails?.provider === 'anonymous';
  };

  const value = {
    currentUser,
    userDetails,
    signup,
    login,
    logout,
    resetPassword,
    isAdmin,
    loginAsGuest,
    isPidUser,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
